// S4.6 — THE QUARANTINE BOARD, /quality/quarantine (spec 07 §4.6, 07-B-3).
//
// Every quarantined item and its disposition. THE WORD IS QUARANTINE (D-123
// ruling 9): the wire says `holds` because renaming a path is an oasdiff
// break; every string on this screen says quarantine, in en and ru together
// (D-122). Module 04's flag is spoken as "quality hold on the operation" and
// module 06's reservations as "reservation" wherever either renders near this
// table — three unrelated things, three words.
//
// A TABLE WITH THREE STATUS TABS, NOT A KANBAN (the D-128-reserved board
// narrowing, stated in §4.6): a disposition here requires a mandatory note and
// three cross-module server calls — a drag that opens a modal is not a drag,
// and a second view of the same rows is a maintenance surface with no
// capability behind it. Active / Released / Disposed are the §2.7 lifecycle,
// asked of §5's `status` filter one at a time, exactly as the inspection queue
// asks its own.
//
// MONEY (D-126 / D-121 / §7): `value_at_risk_minor` + `currency` arrive
// together or not at all — the serializer redacts BY ABSENCE for roles outside
// group_admin/facility_admin/finance/qc/planner, and an unvalued hold is
// absent the same way (unknown ≠ zero, §9.10). An absent pair renders module
// 05's redaction mark (`•••`) — never 0, never a dash that implies zero, and
// never an echoed raw minor amount (a numeric is RENDERED, D-121).
//
// BULK SELECT → ONE DISPOSITION DIALOG (§4.6): the note is validated
// client-side for release/downgrade (the server's 422 is the backstop, never
// the first line), procurement is offered ONLY return_to_supplier
// (`offeredQuarantineDispositions` — the D-040 service narrowing, mirrored),
// and a lost §9.2 race renders the §4.6 concurrent-disposition state FROM THE
// 409 BODY — the winner's kind, disposer and instant — with a refresh prompt.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { RefLabel } from "../../../../components/ref-label";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { Tabs, type TabDef } from "../../../../components/tabs";
import { UnauthorizedError } from "../../../../lib/problem";
import { foldPagedRows } from "../../../../lib/paged-rows";
import { newIdempotencyKey, RequestError, requestIdOf } from "../../../../lib/request";
import { useQueryClient } from "@tanstack/react-query";
import { useOnline } from "../../../floor-pwa/hooks/use-online";
import { FacilityFilter } from "../../production/components/facility-filter";
import { formatMinor, formatTimestamp } from "../../production/lib/format";
import { useFacilities } from "../../inventory/hooks/inventory-queries";
import { UUID_SHAPE } from "../run/failure-action-sheet";
import {
  reworkBlockedSentence,
  reworkPreviewOfQuery,
  reworkVerdictOf,
} from "../run/rework-verdict";
import {
  isQualityReadRefused,
  offeredQuarantineDispositions,
  useQualityPermissions,
  type QuarantineDisposition,
} from "../hooks/permissions";
import {
  dispositionQuarantine,
  qualityKeys,
  reworkTargetOrderNotLive,
  reworkTargetWorkOrderStatusOf,
  useQualityFilterProducts,
  useQualityInspection,
  useQualityReworkTarget,
  useQuarantineList,
  type QualityHold,
  type QualityHoldDispositionRequest,
  type QualityHoldsQuery,
  type QualityHoldStatus,
} from "../hooks/quality-queries";

export const BOARD_TABS = [
  "active",
  "released",
  "disposed",
] as const satisfies readonly QualityHoldStatus[];
export type BoardTab = (typeof BOARD_TABS)[number];

export interface QuarantineSearch {
  tab?: BoardTab;
  facility_id?: string;
  product_id?: string;
  min_age_days?: number;
}

export function validateQuarantineSearch(search: Record<string, unknown>): QuarantineSearch {
  const out: QuarantineSearch = {};
  const tab = search["tab"];
  if (typeof tab === "string" && (BOARD_TABS as readonly string[]).includes(tab)) {
    out.tab = tab as BoardTab;
  }
  for (const key of ["facility_id", "product_id"] as const) {
    const value = search[key];
    if (typeof value === "string" && value.length > 0) out[key] = value;
  }
  const age = search["min_age_days"];
  const ageNumber = typeof age === "number" ? age : typeof age === "string" ? Number(age) : NaN;
  if (Number.isInteger(ageNumber) && ageNumber > 0) out.min_age_days = ageNumber;
  return out;
}

/** §4.6's age — whole DAYS under quarantine, the same unit as §5's
 * `min_age_days` filter (a board is triaged by how long money has been tied
 * up; the queue's hours are the wrong grain for material that sits).
 * An unparsable instant answers `undefined` and the cell renders the DASH,
 * never "0 d" (A9): zero is a value — placed today — and a wire timestamp
 * this client cannot read is not that value (D-121's rule, defensively). */
export function ageDays(placedAt: string, now: number): number | undefined {
  const placed = Date.parse(placedAt);
  if (!Number.isFinite(placed)) return undefined;
  return Math.max(0, Math.floor((now - placed) / 86_400_000));
}

/**
 * The money cell: module 05's redaction-mark idiom (`•••`, waste
 * events.tsx). ABSENCE IS THE SIGNAL — the serializer dropped the pair, for
 * redaction or for an unknown valuation alike — and the mark is the one
 * spelling for "no number here" that cannot be cached as a value (never 0,
 * never a dash that implies zero; quality.css's own note reserved this).
 */
export function atRiskCell(hold: QualityHold, locale: string): string {
  if (hold.value_at_risk_minor === undefined || hold.currency === undefined) return "•••";
  return formatMinor(hold.value_at_risk_minor, hold.currency, locale);
}

/** One selected hold's disposition outcome, classified for the dialog. */
export type BulkOutcome =
  | { readonly kind: "done"; readonly status: QualityHoldStatus }
  | {
      /** The §9.2 loser: the 409 problem body names the winner. */
      readonly kind: "concurrent";
      readonly disposition: string | undefined;
      readonly by: string | undefined;
      readonly at: string | undefined;
      readonly detail: string;
    }
  | {
      readonly kind: "refused";
      /** The problem's stable code — what the A1 rework-target helper and the
       * R1 intent keys branch on. `undefined` means TRANSPORT: no server
       * decision exists (the intent key survives for the replayed retry). */
      readonly code: string | undefined;
      /** The server's own sentence; `undefined` for a transport failure,
       * which the dialog renders as the i18n transport sentence — never the
       * raw "Failed to fetch" (R4). */
      readonly message: string | undefined;
      readonly detail: string | undefined;
    };

/** Read the §9.2 winner off a thrown disposition failure, or classify it. */
export function classifyDispositionFailure(error: unknown): BulkOutcome {
  if (error instanceof RequestError) {
    const body = (error.problem ?? {}) as Record<string, unknown>;
    const str = (key: string): string | undefined => {
      const value = body[key];
      return typeof value === "string" && value.length > 0 ? value : undefined;
    };
    if (error.code === "QUALITY_HOLD_NOT_ACTIVE") {
      return {
        kind: "concurrent",
        disposition: str("disposition"),
        // A human label when the body ever carries one; the uuid is the
        // FALLBACK, not the first choice (R7 — today's serializer sends only
        // the uuid, and an honest uuid beats an invented name).
        by: str("disposition_by_name") ?? str("disposition_by"),
        at: str("disposition_at"),
        detail: error.problem?.detail ?? error.message,
      };
    }
    return {
      kind: "refused",
      code: error.code,
      message: error.message,
      detail: error.problem?.detail,
    };
  }
  // No RequestError means no server answer AT ALL — a transport failure. The
  // raw sentence ("Failed to fetch") is a browser's, not a decision's, and is
  // deliberately NOT carried (R4): the dialog renders the i18n transport line.
  return { kind: "refused", code: undefined, message: undefined, detail: undefined };
}

export function QuarantineBoardScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: "/quality/quarantine" });
  const perms = useQualityPermissions();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<BoardTab>(search.tab ?? "active");
  const [facilityId, setFacilityId] = useState(search.facility_id ?? "");
  const [productId, setProductId] = useState(search.product_id ?? "");
  const [minAge, setMinAge] = useState(
    search.min_age_days !== undefined ? String(search.min_age_days) : "",
  );
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [rowsAccum, setRowsAccum] = useState<readonly QualityHold[]>([]);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);

  const refused = isQualityReadRefused(perms, "quality.hold.read");

  const query: QualityHoldsQuery = useMemo(() => {
    const minAgeDays = Number(minAge);
    return {
      status: tab,
      ...(facilityId.length > 0 ? { facility_id: facilityId } : {}),
      ...(productId.length > 0 ? { product_id: productId } : {}),
      ...(Number.isInteger(minAgeDays) && minAgeDays > 0 ? { min_age_days: minAgeDays } : {}),
      limit: 50,
    };
  }, [tab, facilityId, productId, minAge]);

  // Restart the page walk on any filter change, keyed on WHAT the query asks
  // (the M05-S08 race — see inspections.tsx).
  const queryKey = JSON.stringify(query);
  useEffect(() => {
    setCursors([undefined]);
    setRowsAccum([]);
    setSelected(new Set());
  }, [queryKey]);

  const cursor = cursors[cursors.length - 1];
  const page = useQuarantineList(query, cursor, !refused);

  useEffect(() => {
    if (page.data === undefined) return;
    setRowsAccum((prior) => foldPagedRows(prior, page.data.items));
  }, [page.data]);

  useEffect(() => {
    if (page.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [page.error, navigate]);

  const facilities = useFacilities();
  // The product picker needs module 01's `catalogue.read`, which sales and
  // finance do not hold — for them the fetch fails and the control is ABSENT
  // (a picker that cannot list its options is not a control; the server-side
  // filter itself is unaffected).
  const products = useQualityFilterProducts(!refused);

  const now = Date.now();

  const offeredKinds = offeredQuarantineDispositions(perms);
  const canDisposition =
    (perms.has("quality.hold.disposition") || perms.isFeedError) && offeredKinds.length > 0;

  const activeTab = tab === "active";
  const allSelected = rowsAccum.length > 0 && rowsAccum.every((row) => selected.has(row.id));
  const selectedRows = rowsAccum.filter((row) => selected.has(row.id));

  const tabs: TabDef[] = BOARD_TABS.map((value) => ({
    id: value,
    label: t(`quality.board.tab.${value}`),
  }));

  const columns: DataTableColumn<QualityHold>[] = [
    // Bulk select — ACTIVE tab only, and only for a principal the dialog can
    // serve (D-118: a checkbox that can never lead anywhere is absent).
    ...(activeTab && canDisposition
      ? [
          {
            key: "select",
            header: t("quality.board.colSelect"),
            headerNode: (
              <input
                type="checkbox"
                aria-label={t("quality.board.selectAll")}
                data-testid="quality-board-select-all"
                checked={allSelected}
                onChange={() => {
                  setSelected(allSelected ? new Set() : new Set(rowsAccum.map((row) => row.id)));
                }}
              />
            ),
            render: (row: QualityHold) => (
              <input
                type="checkbox"
                aria-label={t("quality.board.selectOne", { holdNo: row.hold_no })}
                data-testid="quality-board-select"
                checked={selected.has(row.id)}
                onChange={() => {
                  setSelected((prior) => {
                    const next = new Set(prior);
                    if (next.has(row.id)) next.delete(row.id);
                    else next.add(row.id);
                    return next;
                  });
                }}
              />
            ),
          } satisfies DataTableColumn<QualityHold>,
        ]
      : []),
    { key: "hold_no", header: t("quality.board.colNo"), id: true },
    {
      key: "subject",
      header: t("quality.board.colSubject"),
      render: (row) => <HoldSubjectCell row={row} />,
    },
    {
      key: "reason",
      header: t("quality.board.colReason"),
      render: (row) => <span className="quality-board-reason">{row.reason}</span>,
    },
    {
      key: "inspection",
      header: t("quality.board.colInspection"),
      render: (row) =>
        row.inspection_id !== null ? (
          <Link
            to="/quality/inspections/$id"
            params={{ id: row.inspection_id }}
            data-testid="quality-board-inspection-link"
          >
            {t("quality.board.inspectionLink")}
          </Link>
        ) : (
          <span data-testid="quality-board-manual">{t("quality.board.manualHold")}</span>
        ),
    },
    {
      key: "age",
      header: t("quality.board.colAge"),
      age: true,
      numeric: true,
      // A9 — an unreadable placed_at renders the dash, never "0 d".
      render: (row) => {
        const days = ageDays(row.placed_at, now);
        return days === undefined ? (
          <span className="quality-dash">{t("quality.board.noAge")}</span>
        ) : (
          t("quality.board.ageDays", { days })
        );
      },
    },
    {
      key: "value_at_risk",
      header: t("quality.board.colAtRisk"),
      numeric: true,
      render: (row) => (
        <span data-testid="quality-board-atrisk">{atRiskCell(row, i18n.language)}</span>
      ),
    },
    {
      key: "placed_by",
      header: t("quality.board.colPlacedBy"),
      render: (row) => <RefLabel kind="user" id={row.placed_by} copyable />,
    },
    // The agent proposal lives ON the hold (D-128 r4): a draft awaiting qc
    // confirm, superseded by the next proposal, cleared on disposition —
    // rendered here and pre-filling the dialog. Never a state transition.
    ...(activeTab
      ? [
          {
            key: "proposal",
            header: t("quality.board.colProposal"),
            render: (row: QualityHold) =>
              row.proposed_disposition !== null ? (
                <StatusChip variant="accent" dot={false} data-testid="quality-board-proposal">
                  {t("quality.board.proposalChip", {
                    kind: t(`quality.disposition.${row.proposed_disposition}`),
                  })}
                </StatusChip>
              ) : (
                <span className="quality-dash">{t("quality.board.noProposal")}</span>
              ),
          } satisfies DataTableColumn<QualityHold>,
        ]
      : [
          {
            key: "decision",
            header: t("quality.board.colDecision"),
            render: (row: QualityHold) => (
              <span data-testid="quality-board-decision">
                {row.disposition !== null ? t(`quality.disposition.${row.disposition}`) : ""}
                {row.disposition_at !== null
                  ? ` · ${formatTimestamp(row.disposition_at, i18n.language)}`
                  : ""}
              </span>
            ),
          } satisfies DataTableColumn<QualityHold>,
        ]),
  ];

  const afterDispositions = (): void => {
    void queryClient.invalidateQueries({ queryKey: qualityKeys.holdLists() });
    void queryClient.invalidateQueries({ queryKey: qualityKeys.inspectionLists() });
  };

  return (
    <section className="quality-board" data-testid="quality-board">
      <PageHeader title={t("quality.board.title")} />

      <Tabs
        tabs={tabs}
        active={tab}
        label={t("quality.board.tabsAria")}
        data-testid="quality-board-tabs"
        onSelect={(next) => {
          setTab(next as BoardTab);
          void navigate({
            to: "/quality/quarantine",
            search: { ...search, tab: next as BoardTab },
            replace: true,
          });
        }}
      />

      <div className="filter-bar" data-testid="quality-board-filters">
        <FacilityFilter
          value={facilityId}
          onChange={setFacilityId}
          facilities={facilities.data?.items ?? []}
          allowAll
          testId="quality-board-filter-facility"
        />
        {products.data !== undefined && products.data.length > 0 ? (
          <Field label={t("quality.board.filterProduct")}>
            <select
              value={productId}
              data-testid="quality-board-filter-product"
              onChange={(e) => {
                setProductId(e.target.value);
              }}
            >
              <option value="">{t("quality.board.anyProduct")}</option>
              {products.data.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.sku} · {product.name}
                </option>
              ))}
            </select>
          </Field>
        ) : products.error !== null &&
          !(products.error instanceof UnauthorizedError) &&
          !(products.error instanceof RequestError && products.error.httpStatus === 403) ? (
          // A4 / D-118 — a TRANSIENT probe failure is "dead for a fixable
          // reason", so the control stays, disabled, with the reason inside
          // it. Absence remains the PERMISSION answer alone (the 403 branch):
          // sales and finance, whose catalogue probe is refused, still get no
          // control at all.
          <Field label={t("quality.board.filterProduct")}>
            <select disabled data-testid="quality-board-filter-product-dead">
              <option>{t("quality.board.productFilterUnavailable")}</option>
            </select>
          </Field>
        ) : null}
        <Field label={t("quality.board.filterAge")}>
          <input
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            value={minAge}
            data-testid="quality-board-filter-age"
            onChange={(e) => {
              setMinAge(e.target.value);
            }}
          />
        </Field>
      </div>

      {/* Rev-7 (D-448): the decide bar appears once a row is selected, the
          way a DataTable bulk bar does. A dead "Decide 0 selected" button
          with a sentence explaining it sat on screen at all times. */}
      {activeTab && canDisposition && selectedRows.length > 0 ? (
        <div className="quality-toolbar" data-testid="quality-board-actions">
          <Button
            variant="primary"
            data-testid="quality-board-disposition-open"
            onClick={() => {
              setDialogOpen(true);
            }}
          >
            {t("quality.board.dispositionOpen", { count: selectedRows.length })}
          </Button>
        </div>
      ) : null}

      {refused ? (
        <ForbiddenState title={t("quality.access.title")} body={t("quality.access.board")} />
      ) : null}

      {!refused && page.fetchStatus === "paused" && rowsAccum.length === 0 ? (
        <OfflineState title={t("quality.offline.title")} body={t("quality.offline.body")} />
      ) : null}

      {!refused && page.isPending && page.fetchStatus !== "paused" && rowsAccum.length === 0 ? (
        <TableSkeleton rows={5} cols={columns.length} label={t("quality.common.loading")} />
      ) : null}

      {!refused && page.error !== null && !(page.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("quality.board.error")}
          retryLabel={t("quality.common.retry")}
          requestIdLabel={t("quality.common.requestId")}
          requestId={requestIdOf(page.error)}
          onRetry={() => {
            void page.refetch();
          }}
        />
      ) : null}

      {!refused &&
      !page.isPending &&
      page.fetchStatus !== "paused" &&
      page.error === null &&
      rowsAccum.length === 0 ? (
        <EmptyState
          title={t(`quality.board.empty.${tab}Title`)}
          body={t(`quality.board.empty.${tab}Body`)}
        />
      ) : null}

      {rowsAccum.length > 0 ? (
        <>
          <DataTable
            data-testid="quality-board-table"
            columns={columns}
            rows={rowsAccum}
            rowKey={(row) => row.id}
            empty={null}
            caption={t("quality.board.tableCaption")}
            rowTestId="quality-board-row"
          />
          {page.data?.next_cursor != null ? (
            <div className="quality-toolbar">
              <Button
                size="sm"
                data-testid="quality-board-more"
                onClick={() => {
                  const next = page.data?.next_cursor;
                  if (next != null) setCursors((prior) => [...prior, next]);
                }}
              >
                {t("quality.board.loadMore")}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {dialogOpen ? (
        <DispositionDialog
          holds={selectedRows}
          offeredKinds={offeredKinds}
          onDone={afterDispositions}
          onClose={(refresh) => {
            setDialogOpen(false);
            if (refresh) {
              setSelected(new Set());
              setCursors([undefined]);
              setRowsAccum([]);
              afterDispositions();
            }
          }}
        />
      ) : null}
    </section>
  );
}

function HoldSubjectCell({ row }: { row: QualityHold }) {
  const { t } = useTranslation();
  if (row.stock_unit_id !== null) {
    return (
      <span data-testid="quality-board-subject">
        {t("quality.run.subjectUnit")}{" "}
        <RefLabel kind="stock_unit" id={row.stock_unit_id} copyable />
      </span>
    );
  }
  if (row.lot_id !== null) {
    return (
      <span data-testid="quality-board-subject">
        {t("quality.run.subjectLot")} <RefLabel kind="lot" id={row.lot_id} copyable />
      </span>
    );
  }
  // §2.7's CHECK makes this unrepresentable; render honestly if a wire ever
  // carries it rather than crashing the board.
  return <span className="quality-dash">{t("quality.board.noSubject")}</span>;
}

// ---------------------------------------------------------------------------
// The disposition dialog (§4.6): one decision over N selected quarantines.
// ---------------------------------------------------------------------------

/** A1 — is this refusal about the REWORK TARGET? `QUALITY_REWORK_TARGET_*`
 * (the not-reopenable 409) and the §3.5a `QUALITY_SUBJECT_INVALID` family
 * (the "send work_order_operation_id" 422, the does-not-resolve 422, the
 * manual-hold "dispose without an operation ref" 422) all name the field —
 * the dialog renders them AS the field's helper, form still live. */
function isReworkTargetRefusal(outcome: BulkOutcome): boolean {
  return (
    outcome.kind === "refused" &&
    outcome.code !== undefined &&
    (outcome.code.startsWith("QUALITY_REWORK_TARGET_") ||
      outcome.code === "QUALITY_SUBJECT_INVALID")
  );
}

/** §3.5c's scope values, as the dialog offers them (07-D — D-193). */
export const RECEIVING_SCOPES = ["sampled_only", "whole_lot", "inspect_all"] as const;
export type ReceivingScope = (typeof RECEIVING_SCOPES)[number];

/** 07-D — is this refusal about the SCOPE? Both §3.5c 422s name the choice
 * (`QUALITY_SCOPE_REQUIRED` lists the three scopes and what each does;
 * `QUALITY_SCOPE_NOT_APPLICABLE` names the failing clause), so the dialog
 * renders them AS the scope control's helper with the form still live —
 * the same treatment A1 gave the rework target, because a 422 whose field
 * has no control is the dead-end that pattern exists to prevent. The
 * `whole_lot` 409 (`QUALITY_HOLD_ALREADY_ACTIVE` — a lot hold already
 * stands) is deliberately NOT in this set: changing the scope cannot fix
 * it, so it settles as an outcome line with the server's own sentence and
 * the refresh prompt is the continuation. */
function isScopeRefusal(outcome: BulkOutcome): boolean {
  return (
    outcome.kind === "refused" &&
    (outcome.code === "QUALITY_SCOPE_REQUIRED" || outcome.code === "QUALITY_SCOPE_NOT_APPLICABLE")
  );
}

function DispositionDialog({
  holds,
  offeredKinds,
  onDone,
  onClose,
}: {
  readonly holds: readonly QualityHold[];
  readonly offeredKinds: readonly QuarantineDisposition[];
  /** Fired once after the run, whatever the outcomes — server state changed. */
  readonly onDone: () => void;
  readonly onClose: (refresh: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // R4 — the sheet's rule, applied to the board's dialog: a disposition is an
  // ONLINE act (§4.4's dated sentence) and never queues, so a dead confirm
  // carries the same reason the sheet's slabs do.
  const online = useOnline();
  // The single selected hold, when there is one — D-128 r4's proposal
  // pre-fill AND A1's rework-target field both exist only at this shape.
  const only = holds.length === 1 ? holds[0] : undefined;
  const proposedKind = only?.proposed_disposition ?? undefined;
  const [kind, setKind] = useState<QuarantineDisposition | "">(
    proposedKind !== undefined && offeredKinds.includes(proposedKind) ? proposedKind : "",
  );
  const [notes, setNotes] = useState(only?.proposed_notes ?? "");
  const [target, setTarget] = useState("");
  /** The server's own rework-target sentence, rendered AT the field (A1). */
  const [targetHelper, setTargetHelper] = useState<string | undefined>(undefined);
  /** 07-D — §3.5c's second decision, over the SAME dialog. */
  const [scope, setScope] = useState<ReceivingScope | "">("");
  /** The server's own §3.5c sentence, rendered AT the scope control. */
  const [scopeHelper, setScopeHelper] = useState<string | undefined>(undefined);
  const [running, setRunning] = useState(false);
  const [outcomes, setOutcomes] = useState<ReadonlyMap<string, BulkOutcome> | undefined>(undefined);
  // R1 — one Idempotency-Key per hold per INTENT (this body, these holds),
  // reused across a retry carrying the same body, discarded once a
  // non-transport outcome settles. A ref: intents survive re-renders.
  const intentKeys = useRef(new Map<string, { fingerprint: string; key: string }>());

  // A1 — the rework TARGET, offered ONLY where §3.5a can take one and the
  // hold cannot supply its own: a SINGLE selected quarantine that is manual
  // (no linked inspection) or whose inspection names no operation — probed
  // through the §5 hold-detail's inspection read, because the list row does
  // not carry the inspection's operation. After a rework-target refusal the
  // field always shows (the server just said the target is the question).
  //
  // 07-D widens the probe: the §3.5c SCOPE shape also lives on the linked
  // inspection (stage + the lot it names), so a single UNIT-scoped hold
  // probes whatever the kind — one cached GET answers both questions.
  //
  // D-299 ruling G5 widens it once more: whenever REWORK IS OFFERED to a single
  // inspection-linked quarantine, the probe runs BEFORE a kind is picked,
  // because the same read now says whether the rework's target order is still
  // live — and a dead choice is only worth anything if it is dead before the
  // planner picks it, not after.
  const probeEnabled =
    only !== undefined &&
    only.inspection_id !== null &&
    (kind === "rework" || only.stock_unit_id !== null || offeredKinds.includes("rework"));
  const probe = useQualityInspection(only?.inspection_id ?? undefined, probeEnabled);
  // D-299 ruling G5 (spec 07 §4.6's "Rework on a finished order"): a SINGLE
  // selection whose inspection names an operation on an order that is no
  // longer `released`/`in_progress`. §3.5a would answer that rework 409
  // `QUALITY_REWORK_TARGET_NOT_REOPENABLE` (`work_order_not_live`), so the
  // option renders dead with the order's reason at the control, naming the
  // three continuations THIS door takes. A multi-select, a manual quarantine
  // and one whose inspection names no operation cannot know the order before
  // the call (the read answers `null`); the server's per-row sentence renders
  // for those, exactly as every other rework refusal already does.
  const reworkTargetStatus = reworkTargetWorkOrderStatusOf(probe.data);
  const reworkDeadStatus =
    only !== undefined &&
    offeredKinds.includes("rework") &&
    reworkTargetOrderNotLive(reworkTargetStatus)
      ? reworkTargetStatus
      : undefined;
  const offerTarget =
    kind === "rework" &&
    only !== undefined &&
    (only.inspection_id === null ||
      probe.data?.inspection.work_order_operation_id === null ||
      targetHelper !== undefined);

  // OWNER RULING Q4 (D-299 round 4): R8's preview reaches this dialog. The
  // hold door decides a rework on the step it would re-open, exactly as the
  // resolve door does, so the dialog asks the same read §4.4's sheet asks
  // (`GET …/rework-target`) and words the answer with the same builder
  // (`rework-verdict.ts`). The inspection read above answers only the own
  // step's ORDER; the preview adds the step's own status and — for a held
  // step — whether THIS reader may rework it (D-133). What is asked:
  //
  //  * a step TYPED into the target field (the shape that offers it) — by id;
  //  * otherwise the inspection's own step, with no ref, as soon as rework is
  //    offered to a single inspection-linked quarantine (G5's "dead before the
  //    pick") — unless the inspection read already says its order is
  //    finished, which answers without a request;
  //  * nothing for a multi-select, a manual quarantine (the read is per
  //    inspection) or offline. The door's own per-row refusal speaks there.
  //
  // `undefined` = ask nothing; `null` = the door's default (the own step).
  const ownOpId = probe.data?.inspection.work_order_operation_id;
  const typedTarget =
    offerTarget && UUID_SHAPE.test(target.trim()) ? target.trim().toLowerCase() : undefined;
  // The own step is asked about only while nothing else answers it.
  const ownStepTarget: null | undefined =
    typeof ownOpId === "string" && reworkDeadStatus === undefined ? null : undefined;
  const previewTarget: string | null | undefined =
    (only?.inspection_id ?? null) === null || !offeredKinds.includes("rework") || !online
      ? undefined
      : (typedTarget ?? ownStepTarget);
  const previewQuery = useQualityReworkTarget(
    only?.inspection_id ?? undefined,
    previewTarget ?? null,
    // The dialog's rework goes through the QUARANTINE door, so `permitted` is
    // judged on that door's authority (D-224's lineage narrowing), not the
    // sheet's (round 4).
    "hold_disposition",
    previewTarget !== undefined,
  );
  const verdict =
    previewTarget === undefined
      ? undefined
      : reworkVerdictOf(reworkPreviewOfQuery(previewTarget, previewQuery));
  const sentenceOpts = {
    door: "hold",
    canDispose: true,
    locale: i18n.language,
  } as const;

  // THE OWN STEP's verdict makes the OPTION dead, with its reason at the
  // control (G5): there is no field to name another step at this shape, so a
  // blocked own step means rework is not available here at all. The order's
  // reason comes from the inspection read the moment it lands; the step's own
  // status and the hold's who-sentence come from the preview.
  const reworkDeadReason =
    reworkDeadStatus !== undefined
      ? reworkBlockedSentence(
          t,
          { kind: "order_not_live", seqNo: undefined, workOrderStatus: reworkDeadStatus },
          { ...sentenceOpts, canNameTarget: false },
        )
      : previewTarget === null &&
          (verdict?.kind === "order_not_live" ||
            verdict?.kind === "operation_not_completed" ||
            verdict?.kind === "hold_release_required" ||
            verdict?.kind === "not_permitted")
        ? reworkBlockedSentence(t, verdict, { ...sentenceOpts, canNameTarget: false })
        : undefined;
  // A TYPED step's verdict blocks CONFIRM, never the option: the field is a
  // way through, as on the sheet (R8). A pending answer blocks it too, for
  // either target, so a proposal pre-filled as rework cannot be confirmed
  // before the door has been asked.
  const reworkTargetBlockedReason =
    kind !== "rework"
      ? undefined
      : typeof previewTarget === "string" || verdict?.kind === "checking"
        ? reworkBlockedSentence(t, verdict, { ...sentenceOpts, canNameTarget: true })
        : undefined;

  // 07-D — THE SHAPE THAT TAKES A SCOPE (§3.5c AS CODED pt 2): a single
  // UNIT-scoped quarantine whose inspection is stage='receiving' and names a
  // lot. A LOT-scoped receiving hold is D-194's bulk case — its disposition
  // is ALREADY cohort-grain, so the whole control is ABSENT there (all three
  // scopes are refused server-side; absence is the right answer for a
  // genuinely inapplicable control, D-118/D-194 — this is also where
  // "inspect_all is not offered on a bulk receipt" lands, generalised the
  // way the server generalises it). A MULTI-select that happens to include a
  // receiving-sample hold gets the server's own per-row QUALITY_SCOPE_REQUIRED
  // sentence, exactly as multi-select rework refusals render — the control,
  // like the rework target, exists at the single-selection shape only.
  const probedReceivingLot =
    probe.data?.inspection.stage === "receiving" && probe.data.inspection.lot_id !== null;
  const offerScope =
    only !== undefined &&
    only.stock_unit_id !== null &&
    (probedReceivingLot || scopeHelper !== undefined);
  const scopeLotId = probe.data?.inspection.lot_id ?? undefined;

  // §3.5c's table, client-side: release carries a MANDATORY justification and
  // downgrade a MANDATORY note. The server's contract-level 422 is the
  // backstop, never the first line the user meets. Offline outranks them all
  // (R4/D-118): the reason a dead control gives is the one that unblocks it.
  const noteMissing = (kind === "release" || kind === "downgrade") && notes.trim().length === 0;
  const targetInvalid = offerTarget && target.trim().length > 0 && !UUID_SHAPE.test(target.trim());
  // 07-D — §3.5c pt 1: the scope is REQUIRED on the shape that takes it. The
  // client says so first; the server's 422 QUALITY_SCOPE_REQUIRED is the
  // backstop, never the first line the user meets.
  const scopeMissing = offerScope && scope === "";
  const blockedReason = !online
    ? t("quality.run.sheet.offline")
    : kind === ""
      ? t("quality.board.dialogKindRequired")
      : // D-299 G5: a rework already picked (a proposal pre-fill lands before
        // the probe answers) is refused HERE, with the order's reason, rather
        // than posted into the 409.
        kind === "rework" && reworkDeadReason !== undefined
        ? reworkDeadReason
        : noteMissing
          ? kind === "release"
            ? t("quality.board.dialogJustificationRequired")
            : t("quality.board.dialogNoteRequired")
          : scopeMissing
            ? t("quality.board.scopeRequired")
            : targetInvalid
              ? t("quality.board.targetInvalid")
              : reworkTargetBlockedReason;

  const run = async (): Promise<void> => {
    if (kind === "") return;
    setRunning(true);
    const results = new Map<string, BulkOutcome>();
    try {
      for (const hold of holds) {
        const trimmed = notes.trim();
        const trimmedTarget = target.trim();
        const body: QualityHoldDispositionRequest = {
          disposition: kind,
          ...(trimmed.length > 0 ? { disposition_notes: trimmed } : {}),
          ...(kind === "rework" && offerTarget && UUID_SHAPE.test(trimmedTarget)
            ? { work_order_operation_id: trimmedTarget }
            : {}),
          // 07-D — §3.5c's second decision rides the same POST. Sent only at
          // the shape that takes it (the control's own predicate), so a
          // non-receiving disposition never carries a scope the server would
          // refuse QUALITY_SCOPE_NOT_APPLICABLE.
          ...(offerScope && scope !== "" ? { scope } : {}),
        };
        // R1 — the key rides the INTENT: same hold + same body reuses it (a
        // retry replays); a changed body is a new intent and mints fresh.
        const fingerprint = JSON.stringify(body);
        const prior = intentKeys.current.get(hold.id);
        const key = prior?.fingerprint === fingerprint ? prior.key : newIdempotencyKey();
        intentKeys.current.set(hold.id, { fingerprint, key });
        try {
          const terminal = await dispositionQuarantine(hold.id, body, key);
          results.set(hold.id, { kind: "done", status: terminal.status });
        } catch (error) {
          if (error instanceof UnauthorizedError) {
            // R3 — the session died mid-run: go sign in (the read paths'
            // redirect). The finally below still settles the dialog honestly
            // — running off, caches told — instead of freezing it.
            void navigate({ to: "/login" });
            return;
          }
          results.set(hold.id, classifyDispositionFailure(error));
        }
        const outcome = results.get(hold.id);
        // R1 — discard the intent once the server DECIDED (done or a coded
        // refusal: the store would replay that answer under this key even
        // after the user fixes the body); keep it only across transport
        // failures, where the retry must replay the same key.
        if (outcome !== undefined && !(outcome.kind === "refused" && outcome.code === undefined)) {
          intentKeys.current.delete(hold.id);
        }
        // Render progress as it lands — a bulk run over N holds should not
        // look frozen while the fourth call is in flight.
        setOutcomes(new Map(results));
      }
    } finally {
      // R3 — whatever happened, the dialog settles: the spinner stops and the
      // board's caches are told (any prefix of the run may have committed).
      setRunning(false);
      onDone();
    }
    // A1 — a single-hold rework refused ABOUT ITS TARGET re-arms the form
    // with the server's sentence at the field, instead of dead-ending in an
    // outcome line the user cannot act on.
    if (only !== undefined && kind === "rework") {
      const outcome = results.get(only.id);
      if (outcome !== undefined && isReworkTargetRefusal(outcome)) {
        setTargetHelper(
          outcome.kind === "refused" ? (outcome.detail ?? outcome.message) : undefined,
        );
        setOutcomes(undefined);
      }
    }
    // 07-D — the same re-arm for a single-hold §3.5c refusal: both scope 422s
    // name the choice, so the server's sentence renders AT the control with
    // the form live. The whole_lot 409 (QUALITY_HOLD_ALREADY_ACTIVE) is
    // deliberately excluded — see isScopeRefusal.
    if (only !== undefined) {
      const outcome = results.get(only.id);
      if (outcome !== undefined && isScopeRefusal(outcome)) {
        setScopeHelper(
          outcome.kind === "refused" ? (outcome.detail ?? outcome.message) : undefined,
        );
        setOutcomes(undefined);
      }
    }
  };

  const finished = outcomes?.size === holds.length && !running;
  const anyConcurrent = [...(outcomes?.values() ?? [])].some((o) => o.kind === "concurrent");

  return (
    <Modal
      label={t("quality.board.dialogTitle", { count: holds.length })}
      heading={t("quality.board.dialogTitle", { count: holds.length })}
      closeLabel={t("quality.common.cancel")}
      className="quality-modal"
      testId="quality-board-dialog"
      onClose={() => {
        onClose(outcomes !== undefined);
      }}
    >
      <ul className="quality-board-dialog-holds" data-testid="quality-board-dialog-holds">
        {holds.map((hold) => {
          const outcome = outcomes?.get(hold.id);
          return (
            <li key={hold.id} className="quality-board-dialog-hold">
              <span className="quality-board-dialog-holdno">{hold.hold_no}</span>{" "}
              <span className="quality-board-dialog-holdreason">{hold.reason}</span>
              {outcome !== undefined ? <OutcomeLine outcome={outcome} /> : null}
            </li>
          );
        })}
      </ul>

      {outcomes === undefined ? (
        <>
          {proposedKind !== undefined ? (
            <p className="quality-note" data-testid="quality-board-dialog-proposed">
              {t("quality.board.dialogProposed", {
                kind: t(`quality.disposition.${proposedKind}`),
              })}
            </p>
          ) : null}
          <Field label={t("quality.board.dialogKind")}>
            <select
              value={kind}
              data-testid="quality-board-dialog-kind"
              onChange={(e) => {
                setKind(e.target.value as QuarantineDisposition | "");
              }}
            >
              <option value="">{t("quality.board.dialogKindPick")}</option>
              {offeredKinds.map((value) => (
                <option
                  key={value}
                  value={value}
                  // D-299 G5: present (the principal holds the grant) and dead.
                  disabled={value === "rework" && reworkDeadReason !== undefined}
                  data-testid={`quality-board-dialog-kind-${value}`}
                >
                  {t(`quality.disposition.${value}`)}
                </option>
              ))}
            </select>
          </Field>
          {/* THE ORDER'S REASON, AT THE CONTROL (D-299 G5). Not repeated while
              rework is the picked kind: the blocked line under Confirm says it
              then, and one sentence twice reads as two problems. */}
          {reworkDeadReason !== undefined && kind !== "rework" ? (
            <p className="quality-hint" role="status" data-testid="quality-board-rework-dead">
              {reworkDeadReason}
            </p>
          ) : null}
          {offerScope ? (
            // 07-D — §3.5c's REQUIRED second decision, at the one shape that
            // takes it (a single receiving-sample quarantine): the failure
            // was judged on one unit while the whole cohort stands fenced by
            // the lot's qc_status, and this choice decides the cohort.
            <>
              <Field label={t("quality.board.scopeLabel")}>
                <select
                  value={scope}
                  data-testid="quality-board-dialog-scope"
                  onChange={(e) => {
                    setScope(e.target.value as ReceivingScope | "");
                    // The helper answered the PREVIOUS choice — a new pick
                    // starts a new answer to the server's question.
                    setScopeHelper(undefined);
                  }}
                >
                  <option value="">{t("quality.board.scopePick")}</option>
                  {RECEIVING_SCOPES.map((value) => (
                    <option key={value} value={value}>
                      {t(`quality.board.scope.${value}`)}
                    </option>
                  ))}
                </select>
              </Field>
              {scope === "whole_lot" && scopeLotId !== undefined ? (
                // D-193 — the confirmation copy NAMES THE LOT, because a lot
                // is a SUPERSET of a receipt: one that took two deliveries
                // condemns both, and the operator is told before pressing.
                <p className="quality-note" role="status" data-testid="quality-board-scope-lot">
                  {t("quality.board.scopeLotNote")} <code className="ref-id">{scopeLotId}</code>
                </p>
              ) : null}
              {scopeHelper !== undefined ? (
                // The server's own §3.5c sentence, AT the control.
                <p className="quality-hint" role="alert" data-testid="quality-board-scope-helper">
                  {scopeHelper}
                </p>
              ) : null}
            </>
          ) : null}
          {offerTarget ? (
            // A1 — §3.5a's rework target (`work_order_operation_id`, the §5
            // disposition row), for the one shape that needs it: a manual
            // quarantine or one whose inspection names no operation.
            <Field label={t("quality.board.targetLabel")} wide>
              <input
                value={target}
                placeholder={t("quality.board.targetHint")}
                data-testid="quality-board-dialog-target"
                onChange={(e) => {
                  setTarget(e.target.value);
                  // The helper was about the PREVIOUS target — editing starts
                  // a new answer to the server's question.
                  setTargetHelper(undefined);
                }}
              />
            </Field>
          ) : null}
          {offerTarget && targetHelper !== undefined ? (
            // The server's own rework-target sentence, AT the field (A1).
            <p className="quality-hint" role="alert" data-testid="quality-board-target-helper">
              {targetHelper}
            </p>
          ) : null}
          {/* Q4: the typed step the door WOULD re-open, said back by its
              sequence number — only for a verdict that blocks nothing, as on
              the sheet (never beside a Confirm the same read blocked). */}
          {kind === "rework" && typedTarget !== undefined && verdict?.kind === "live" ? (
            <p className="quality-note" role="status" data-testid="quality-board-target-ok">
              {t("quality.run.sheet.reworkTargetLive", { seq: verdict.seqNo })}
            </p>
          ) : null}
          <Field label={t("quality.board.dialogNotes")} wide>
            <input
              value={notes}
              data-testid="quality-board-dialog-notes"
              onChange={(e) => {
                setNotes(e.target.value);
              }}
            />
          </Field>
          <div className="quality-toolbar">
            <Button
              variant={kind === "scrap" ? "danger" : "primary"}
              data-testid="quality-board-dialog-confirm"
              disabled={blockedReason !== undefined || running}
              busy={running}
              busyLabel={t("quality.common.saving")}
              onClick={() => {
                void run();
              }}
            >
              {t("quality.board.dialogConfirm", { count: holds.length })}
            </Button>
          </div>
          {/* D-118 — the dead confirm says why, at the control. */}
          {blockedReason !== undefined ? (
            <p className="quality-hint" role="status" data-testid="quality-board-dialog-blocked">
              {blockedReason}
            </p>
          ) : null}
        </>
      ) : null}

      {finished ? (
        <div className="quality-toolbar">
          {anyConcurrent ? (
            <p className="quality-note" data-testid="quality-board-refresh-prompt">
              {t("quality.board.refreshPrompt")}
            </p>
          ) : null}
          <Button
            variant="primary"
            data-testid="quality-board-refresh"
            onClick={() => {
              onClose(true);
            }}
          >
            {t("quality.board.refreshBoard")}
          </Button>
        </div>
      ) : null}
    </Modal>
  );
}

/** One hold's outcome line inside the dialog. The concurrent case is §4.6's
 * own state, rendered from the 409 body: the winner's kind, disposer, instant
 * — and how long ago, at the grain the instant deserves (R7): "just now"
 * under a minute, minutes under an hour, hours beyond — because "already
 * disposed 40 minutes ago" is what makes a qc lead put the mouse down rather
 * than retry, and "0 h ago" said the opposite of what it meant. */
function OutcomeLine({ outcome }: { outcome: BulkOutcome }) {
  const { t } = useTranslation();
  if (outcome.kind === "done") {
    return (
      <span className="quality-board-outcome-ok" data-testid="quality-board-outcome-done">
        {t(`quality.board.outcomeDone.${outcome.status}`)}
      </span>
    );
  }
  if (outcome.kind === "concurrent") {
    const atMs = outcome.at !== undefined ? Date.parse(outcome.at) : NaN;
    const agoMs = Number.isFinite(atMs) ? Math.max(0, Date.now() - atMs) : undefined;
    const line = (): string => {
      if (outcome.disposition === undefined || outcome.by === undefined || agoMs === undefined) {
        return outcome.detail;
      }
      const kindLabel = t(`quality.disposition.${outcome.disposition}`);
      const minutes = Math.floor(agoMs / 60_000);
      if (minutes < 1) {
        return t("quality.board.concurrentJustNow", { kind: kindLabel, user: outcome.by });
      }
      if (minutes < 60) {
        return t("quality.board.concurrentMinutes", {
          kind: kindLabel,
          user: outcome.by,
          minutes,
        });
      }
      return t("quality.board.concurrent", {
        kind: kindLabel,
        user: outcome.by,
        hours: Math.floor(agoMs / 3_600_000),
      });
    };
    return (
      <span
        className="quality-board-outcome-bad"
        role="alert"
        data-testid="quality-board-concurrent"
      >
        {line()}
      </span>
    );
  }
  // A transport failure carries no server sentence — the i18n line renders,
  // never the browser's "Failed to fetch" (R4).
  return (
    <span
      className="quality-board-outcome-bad"
      role="alert"
      data-testid="quality-board-outcome-refused"
    >
      {outcome.message ?? t("quality.board.transportFailed")}
      {outcome.message !== undefined &&
      outcome.detail !== undefined &&
      outcome.detail !== outcome.message
        ? ` — ${outcome.detail}`
        : ""}
    </span>
  );
}
